import { useEffect, useState } from 'react';
import { DeploymentState } from 'common/CommonConstants';
import { StatusItemState } from 'common/status/StatusDocument';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { useDeploymentEnabledState } from 'client/pages/deployment/useDeploymentEnabledState';

interface UseDeploymentWarningParams {
  containers: { itemStateID: StatusItemState }[];
}

/**
 * Helper hook to make showing "deployment is starting" & "deployment is stopped" warnings easier.
 */
export const useDeploymentWarning = ({ containers }: UseDeploymentWarningParams) => {
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const statusTree = useDeploymentStatusTree(deploymentID);
  const { enabled } = useDeploymentEnabledState({ deploymentID });

  // Used to hightlight buttons when status turns from loading to ready
  const [loadingToReadyState, setLoadingToReadyState] = useState<'idle' | 'loading' | 'ready'>('idle');

  // Check if all given containers are green
  const isContainersRunning = containers.length > 0 && !containers.some((item) => item.itemStateID !== StatusItemState.GREEN);

  const isRunning = statusTree?.deploymentStateID === DeploymentState.RUNNING;
  const isWarningDisplayed = enabled && (!isRunning || !isContainersRunning);
  const isErrorDisplayed = !enabled;

  useEffect(() => {
    if (enabled && isRunning && isWarningDisplayed && loadingToReadyState === 'idle') {
      setLoadingToReadyState('loading');
    } else if (enabled && isRunning && !isWarningDisplayed && loadingToReadyState === 'loading') {
      setLoadingToReadyState('ready');
    }
  }, [enabled, isRunning, isWarningDisplayed, loadingToReadyState]);

  return { isWarningDisplayed, isErrorDisplayed, loadingToReadyState };
};
